/**
 * Chip <-> Jinja conversion for the styled template editor.
 *
 * `chipHtml` turns the literal Jinja in a content-only body into non-editable chip spans
 * (so `{{ fields }}` / `{% logic %}` show as labelled pills); `unchip` is its exact inverse,
 * decoding each chip's `data-src` back to the original Jinja. Kept pure and separate so the
 * round-trip is unit-tested.
 */

const TOKEN = /\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}/g;

export function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** A raw Jinja snippet → a non-editable chip (display label + URI-encoded source). */
export function chipFor(raw: string): string {
  const enc = encodeURIComponent(raw);
  if (raw.startsWith("{%")) {
    const inner = raw.replace(/^\{%\s*/, "").replace(/\s*%\}$/, "");
    return `<span class="tok tok-tag" contenteditable="false" data-src="${enc}">${escapeHtml(inner)}</span>`;
  }
  const inner = raw.replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "");
  const sign = inner.match(/^sign_field\(\s*["']([^"']+)["']/);
  if (sign) {
    return `<span class="tok tok-sign" contenteditable="false" data-src="${enc}">✎ ${escapeHtml(sign[1])} signature</span>`;
  }
  return `<span class="tok tok-var" contenteditable="false" data-src="${enc}">${escapeHtml(inner)}</span>`;
}

export function chipHtml(body: string): string {
  return body.replace(TOKEN, (m) => chipFor(m));
}

/** Chips → their literal Jinja source (order-independent attribute match). */
export function unchip(html: string): string {
  return html.replace(
    /<span\b(?=[^>]*class="[^"]*\btok\b)[^>]*\bdata-src="([^"]*)"[^>]*>[\s\S]*?<\/span>/g,
    (_, enc) => decodeURIComponent(enc),
  );
}
